import { memo } from 'react';
import { useInView } from 'react-intersection-observer';
import MiscLocale from 'locale/en/misc.json';
import { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';

const MeetScicareStyles = createGlobalStyle`
  .pds08-meet-scicare {
  max-width: none !important;
  padding: 0 !important;
  background-color: var(--fill-secondary);

  > .container {
    max-width: 1440px;
    padding: 50px 30px 50px 30px;
    margin: 0px auto;

    @media ${mqLargeMin} {
      padding: 60px 50px 80px 50px;
    }
  }

  h2 {
    font-size: 26px;
    color: var(--text-primary);
    margin-bottom: 30px;

    @media ${mqLargeMin} {
      font-size: 30px;
    }
  }

  p {
    color: var(--text-primary);
  }

  strong {
    color: var(--text-primary);
  }

  .grid-container {
    display: grid;
    gap: 30px;
    grid-template-columns: auto;
    grid-template-rows: auto auto auto;
    grid-template-areas: 'top' 'aside' 'bottom';

    @media ${mqLargeMin} {
      gap: 30px 50px;
      grid-template-columns: 400px auto;
      grid-template-rows: auto 1fr;
      grid-template-areas:
        'top aside'
        'bottom aside';
    }
  }

  .top {
    grid-area: top;
  }

  .bottom {
    grid-area: bottom;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    p {
      margin-bottom: 30px;

      @media ${mqLargeMin} {
        margin-top: 8px;
      }
    }

    button {
      position: relative;
      left: -15px;
      width: calc(100% + 30px);
      background-color: var(--fill-secondary);
      color: var(--text-primary);
      border: 1px solid var(--outline-primary);
      border-radius: 28px;
      padding: 12px 25px;
      transition:
        background-color 0.25s ease,
        color 0.25s ease;
      font-weight: 500;

      &:focus,
      &:active {
        background-color: var(--fill-brand);
        color: white;
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: var(--fill-brand);
          color: white;
          cursor: pointer;
        }
      }

      @media ${mqLargeMin} {
        left: 0;
        width: fit-content;
      }
    }
  }

  .aside {
    position: relative;
    left: -15px;
    width: calc(100% + 30px);
    grid-area: aside;
    border-radius: 32px;
    background: var(--fill-secondary);
    box-shadow:
      inset 20px 7px 32px rgba(255, 255, 255, 0.75),
      inset 50px 47px 90px -20px #f6f5ef,
      inset -18px -19px 51px #cdccc3;
    padding: 30px;
    display: flex;
    flex-direction: column;

    @media ${mqLargeMin} {
      padding: 50px;
      left: 0;
      width: 100%;
    }

    .bubble {
      padding: 8px 16px;
      width: fit-content;
      max-width: calc(min(300px, 95%));
      border-radius: 18px;
      font-weight: 300;
      margin-bottom: 1px;
      font-size: 13px;
      transform: translateY(20px);
      opacity: 0;
      transition:
        transform 0.25s ease,
        opacity 0.25s ease;

      @media ${mqLargeMin} {
        font-size: 16px;
        padding: 16px 32px;
        max-width: 500px;
      }

      &.response {
        background-color: var(--fill-brand);
        color: white;

        &.pointed {
          border-bottom-left-radius: 0px;
        }
      }

      &.query {
        background-color: white;
        color: var(--text-primary);
        align-self: flex-end;

        &.pointed {
          border-bottom-right-radius: 0px;
        }
      }

      &.pointed {
        margin-bottom: 10px;
      }
    }

    &.visible {
      .bubble {
        transform: translateY(0px);
        opacity: 1;

        &:nth-of-type(2) {
          transition-delay: 1500ms;
        }

        &:nth-of-type(3) {
          transition-delay: 2900ms;
        }

        &:nth-of-type(4) {
          transition-delay: 3300ms;
        }
      }
    }
  }
}
`;

const PDS08MeetScicare = () => {
  const mailto = MiscLocale.scicare_mailto;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  return (
    <section className='pds08-meet-scicare' ref={ref}>
      <MeetScicareStyles />
      <div className='container'>
        <h2>Meet SciCare</h2>
        <div className='grid-container'>
          <div className='top'>
            <p>
              Our team of experts in scientific communication and education—here
              for anyone and everyone in our community.
            </p>
          </div>

          <div className={`aside ${inView ? 'visible' : ''}`}>
            <div className='bubble response pointed'>
              Hi, we&apos;re SciCare. We&apos;re your team of experts in
              scientific communication and education. We know how hard it is to
              get answers so we review, research, and answer health and
              science-related questions from our community.
            </div>
            <div className='bubble query pointed'>
              Oh, that&apos;s cool—even if I&apos;m not a member yet, can I
              still ask you questions?
            </div>
            <div className='bubble response'>
              Absolutely, that&apos;s what we&apos;re here for 🤓
            </div>
            <div className='bubble response pointed'>
              We believe better information=better choices, so ask away!
            </div>
          </div>

          <div className='bottom'>
            <div className='content'>
              <strong>Pediatricians + Health Practitioners</strong>
              <p>
                Ask us about dosages, medication interactions, clinical
                research, and more.
              </p>
              <strong>Parents + Caregivers</strong>
              <p>
                Ask us anything—we know how hard making the right decisions can
                be on a minute-by-minute basis.
              </p>
            </div>
            <button
              onClick={(e) => {
                // TSREVIEW:
                // was `window.location = mailto` (TS ERROR)
                // it should be `window.location.href`
                window.location.href = mailto;
                e.preventDefault();
              }}
              type='button'
            >
              💬 Email SciCare
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(PDS08MeetScicare);
